import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { API } from "../../App";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
};

const selectCls = "block h-9 w-full rounded-md border border-slate-700 bg-slate-900 px-2 text-sm text-slate-100";
const NO_CLASS = "__none__";

export default function TrainingDiagnosticsPanel() {
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [classId, setClassId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Lists for the cascade: school -> class -> student (all already available to the super-admin).
  useEffect(() => {
    const cfg = { withCredentials: true };
    Promise.all([
      axios.get(`${API}/super-admin/schools`, cfg),
      axios.get(`${API}/admin/classes`, cfg),
      axios.get(`${API}/admin/users`, cfg),
    ])
      .then(([sc, cl, us]) => {
        setSchools(sc.data || []);
        setClasses(cl.data || []);
        setStudents((us.data || []).filter((u) => u.role === "student"));
      })
      .catch(() => toast.error("Não foi possível carregar escolas, turmas e alunos"));
  }, []);

  const schoolClasses = useMemo(() => classes.filter((c) => c.school_id === schoolId), [classes, schoolId]);
  const schoolStudents = useMemo(() => students.filter((u) => u.school_id === schoolId), [students, schoolId]);
  const classStudents = useMemo(() => {
    if (!classId) return [];
    const list = classId === NO_CLASS ? schoolStudents.filter((u) => !u.class_id) : schoolStudents.filter((u) => u.class_id === classId);
    return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt"));
  }, [schoolStudents, classId]);
  const hasNoClass = useMemo(() => schoolStudents.some((u) => !u.class_id), [schoolStudents]);

  const pickSchool = (v) => { setSchoolId(v); setClassId(""); setStudentId(""); setData(null); };
  const pickClass = (v) => { setClassId(v); setStudentId(""); setData(null); };

  // API is read inside the function on purpose (circular import with App.js: see AerofleetPanel).
  const run = async () => {
    setLoading(true);
    setData(null);
    try {
      const r = await axios.get(`${API}/super-admin/training-diagnostics`, {
        params: { q: studentId }, withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao obter o diagnóstico");
    } finally {
      setLoading(false);
    }
  };

  const a = data?.analysis;
  return (
    <div className="space-y-4" data-testid="training-diagnostics-panel">
      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Diagnóstico de treino · perguntas repetidas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-xs">
          <p className="text-slate-400">
            Mostra o que as últimas 10 sessões de treino de um aluno realmente serviram: quanto se repete, que perguntas
            voltam e qual o tamanho real do conjunto de onde o treino pode escolher. Só leitura.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs">Escola</Label>
              <select className={selectCls} value={schoolId} onChange={(e) => pickSchool(e.target.value)} data-testid="diagnostics-school">
                <option value="">Escolher…</option>
                {schools.map((sc) => <option key={sc.school_id} value={sc.school_id}>{sc.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Turma</Label>
              <select className={selectCls} value={classId} onChange={(e) => pickClass(e.target.value)} disabled={!schoolId} data-testid="diagnostics-class">
                <option value="">Escolher…</option>
                {schoolClasses.map((c) => <option key={c.class_id} value={c.class_id}>{c.name}</option>)}
                {hasNoClass && <option value={NO_CLASS}>Sem turma</option>}
              </select>
            </div>
            <div>
              <Label className="text-xs">Aluno</Label>
              <select className={selectCls} value={studentId} onChange={(e) => { setStudentId(e.target.value); setData(null); }} disabled={!classId} data-testid="diagnostics-student">
                <option value="">Escolher…</option>
                {classStudents.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
              </select>
            </div>
            <Button size="sm" className="btn-cockpit text-xs" disabled={loading || !studentId} onClick={run} data-testid="diagnostics-run">
              <Search className="w-3 h-3 mr-1.5" /> {loading ? "A analisar..." : "Analisar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <>
          <Card className="instrument-panel" data-testid="diagnostics-findings">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{data.student.name} · {data.student.email}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-xs text-slate-300">
              {data.findings.map((f, i) => <p key={i}>• {f}</p>)}
              <p className="text-slate-500 pt-2">
                Sessões analisadas: {a.sessions_analysed} · perguntas servidas: {a.total_slots} · distintas: {a.distinct_questions}
                · normal: {a.modes.normal} · avaliação: {a.modes.evaluation}
              </p>
            </CardContent>
          </Card>

          <Card className="instrument-panel">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Cadernos</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto text-xs">
              <table className="w-full text-left">
                <thead className="text-slate-400">
                  <tr><th className="pr-3">Caderno</th><th className="pr-3">Disponíveis</th><th className="pr-3">Vistas</th><th className="pr-3">Dominadas</th><th className="pr-3">Última errada</th><th>Modo</th></tr>
                </thead>
                <tbody>
                  {data.cadernos.map((c) => (
                    <tr key={c.caderno_id} className="border-t border-slate-800">
                      <td className="pr-3 py-1">{c.caderno_id}</td>
                      <td className="pr-3">{c.pool_usable} / {c.pool_total}</td>
                      <td className="pr-3">{c.coverage_pct}%</td>
                      <td className="pr-3">{c.mastery_pct}%</td>
                      <td className="pr-3">{c.last_answer_wrong}</td>
                      <td>{c.maintenance_mode ? "manutenção" : "normal"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card className="instrument-panel">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Sessões (mais recente primeiro)</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto text-xs">
              <table className="w-full text-left">
                <thead className="text-slate-400">
                  <tr><th className="pr-3">Início</th><th className="pr-3">Perguntas</th><th className="pr-3">Modo</th><th className="pr-3">Concluída</th><th className="pr-3">Nota</th><th>Repetidas da anterior</th></tr>
                </thead>
                <tbody>
                  {a.sessions.map((s, i) => (
                    <tr key={i} className="border-t border-slate-800">
                      <td className="pr-3 py-1">{fmt(s.started_at)}</td>
                      <td className="pr-3">{s.questions}</td>
                      <td className="pr-3">{s.evaluation_mode ? "avaliação" : "normal"}</td>
                      <td className="pr-3">{s.completed ? "sim" : "não"}</td>
                      <td className="pr-3 whitespace-nowrap" data-testid="diagnostics-session-score"
                        title={s.session_score != null ? `Pontuação da sessão: ${s.session_score}` : undefined}>
                        {s.score_pct === null || s.score_pct === undefined
                          ? "—"
                          : s.completed
                            ? `${s.correct}/${s.questions} · ${s.score_pct}%`
                            : <span className="text-slate-400">{`parcial: ${s.correct}/${s.answered} · ${s.score_pct}%`}</span>}
                      </td>
                      <td>{s.overlap_with_previous === null ? "—" : `${s.overlap_with_previous} (${s.overlap_pct}%)`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {a.top_repeated.length > 0 && (
            <Card className="instrument-panel" data-testid="diagnostics-top-repeated">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Perguntas que mais se repetem</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-xs text-slate-300">
                {a.top_repeated.map((t) => (
                  <div key={t.question_id} className="border-t border-slate-800 pt-2">
                    <p><strong>{t.times_in_window}×</strong> · {t.section || "—"} · vista {t.times_seen_ever}× no total · última resposta: {t.last_answer_correct === true ? "certa" : t.last_answer_correct === false ? "errada" : "—"}</p>
                    <p className="text-slate-400 break-words">{t.statement}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
